import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiFileText, FiPlayCircle, FiInfo, FiRadio, FiVideo, FiActivity, FiArrowRight } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import LessonContentModal from '../../components/student/LessonContentModal';

const PremiumStyles = () => (
    <style>{`
        .glass {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(20px) saturate(180%);
            -webkit-backdrop-filter: blur(20px) saturate(180%);
        }
        @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
            animation: spin-slow 8s linear infinite;
        }
    `}</style>
);

export default function StudentLessonView() {
    const { monthId } = useParams();
    const [lessons, setLessons] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeContent, setActiveContent] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [securitySettings, setSecuritySettings] = useState({ screen_recording_watermark: '1' });

    const getContentStyles = (sub) => {
        if (sub.pdf_url) return {
            color: 'text-emerald-600',
            bg: 'bg-emerald-50',
            border: 'border-emerald-100',
            hover: 'hover:bg-emerald-100',
            icon: <FiFileText />,
            label: 'PDF Document',
            type: 'pdf'
        };
        const isLive = sub.title?.toLowerCase().includes('live') || sub.zoom_link;
        if (isLive) return {
            color: 'text-red-600',
            bg: 'bg-red-50',
            border: 'border-red-100',
            hover: 'hover:bg-red-100',
            icon: <FiRadio className="animate-pulse" />,
            label: 'Live Class',
            type: 'video'
        };
        return {
            color: 'text-blue-600',
            bg: 'bg-blue-50',
            border: 'border-blue-100',
            hover: 'hover:bg-blue-100',
            icon: <FiPlayCircle />,
            label: 'Recording',
            type: 'video'
        };
    };

    React.useEffect(() => {
        const loadPageData = async () => {
            try {
                const [lessonsData, settingsData] = await Promise.all([
                    fetchAPI(`/student/get_lessons.php?month_id=${monthId}`),
                    fetchAPI('/admin/get_settings.php')
                ]);

                setLessons(lessonsData.lessons);
                setMetadata(lessonsData.metadata);
                setSecuritySettings(settingsData);
            } catch (err) {
                console.error("Failed to load page data", err);
            } finally {
                setLoading(false);
            }
        };
        loadPageData();
    }, [monthId]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-bold animate-pulse poppins">Loading Academic Materials...</p>
        </div>
    );

    if (!lessons.length && !metadata) return (
        <div className="p-12 text-center bg-white rounded-3xl border border-dashed border-gray-200 m-8">
            <FiActivity size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-800 poppins">No content available</h3>
            <p className="text-gray-500 mt-2 poppins">Check back later for new lessons and materials.</p>
        </div>
    );

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#fafbff]">
            <PremiumStyles />
            {/* Ultra-Modern Background Elements */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-[120px] opacity-40 animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-50 rounded-full blur-[150px] opacity-30"></div>
                <div className="absolute top-[20%] right-[5%] w-[30%] h-[30%] bg-blue-50 rounded-full blur-[100px] opacity-20 animate-bounce duration-[10s]"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-3 md:py-5 animate-in fade-in slide-in-from-bottom-6 duration-1000">
                {/* Drastically Compact Glassmorphic Header */}
                <div className="mb-5 md:mb-6 glass px-4 py-3 md:px-6 md:py-4 rounded-2xl border border-white/60 shadow-lg backdrop-blur-3xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                        <div className="space-y-1.5">
                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-600 text-white rounded-md text-[8px] md:text-[9px] font-black uppercase tracking-[0.1em] shadow-md shadow-blue-200">
                                <FiActivity size={8} className="animate-spin-slow" />
                                <span>Academy Portal</span>
                            </div>


                            <h1 className="text-lg md:text-xl lg:text-2xl font-black text-gray-900 tracking-tight leading-tight uppercase lemon-milk">
                                {metadata?.month_name || 'Academic'} <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Curriculum</span>
                            </h1>
                        </div>

                        <div className="flex items-center gap-2 text-gray-600 poppins font-bold">
                            <span className="flex items-center gap-1.5 text-[10px] md:text-[11px] bg-white/40 px-2 py-0.5 rounded-lg border border-white/50 backdrop-blur-sm">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_5px_rgba(59,130,246,0.6)]" />
                                {metadata?.class_name || 'Mathematics'}
                            </span>
                            <span className="flex items-center gap-1.5 text-[10px] md:text-[11px] bg-white/40 px-2 py-0.5 rounded-lg border border-white/50 backdrop-blur-sm">
                                <span className="text-gray-400">Grade</span> {metadata?.grade || 'N/A'}
                            </span>
                        </div>
                    </div>


                    <div className="flex items-center gap-3">
                        <div className="px-3 py-1.5 md:px-4 md:py-2 bg-white shadow-md shadow-blue-100/40 rounded-xl md:rounded-2xl border border-gray-100 flex items-center gap-2.5 hover:scale-105 transition-transform duration-500">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                                <FiActivity size={14} />
                            </div>
                            <div>
                                <p className="text-[7px] md:text-[8px] text-gray-400 font-black uppercase tracking-widest leading-none mb-0.5">Content</p>
                                <p className="text-xs md:text-sm font-black text-gray-900 leading-none">{lessons.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Compact Roadmap Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {lessons.map((lesson, lIdx) => (
                        <div
                            key={lesson.id}
                            style={{ transitionDelay: `${lIdx * 100}ms` }}
                            className="group/card relative bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-lg transition-all duration-700 overflow-hidden flex flex-col hover:-translate-y-1 animate-in fade-in slide-in-from-bottom-8"
                        >
                            {/* Card Decorative Label - Scaled Down */}
                            <div className="absolute top-4 right-4 text-[20px] md:text-[24px] font-black text-gray-50 opacity-10 poppins select-none group-hover/card:text-blue-100 group-hover/card:opacity-30 transition-all">
                                {String(lIdx + 1).padStart(2, '0')}
                            </div>

                            <div className="p-4 md:p-5 relative z-10">
                                <div className="mb-4">
                                    <div className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[8px] font-black uppercase tracking-widest mb-1.5">
                                        Session {String(lIdx + 1).padStart(2, '0')}
                                    </div>
                                    <h3 className="text-sm md:text-base font-black text-gray-900 leading-tight group-hover/card:text-blue-600 transition-colors duration-500 poppins">
                                        {lesson.title}
                                    </h3>
                                </div>

                                <div className="space-y-2">
                                    {lesson.sub_items.map((sub, idx) => {
                                        const styles = getContentStyles(sub);
                                        return (
                                            <button
                                                key={sub.id || idx}
                                                onClick={() => {
                                                    const url = sub.pdf_url || sub.recording_url || sub.zoom_link;
                                                    setActiveContent({
                                                        type: styles.type,
                                                        url,
                                                        title: sub.title,
                                                        description: sub.description || lesson.description
                                                    });
                                                    setIsModalOpen(true);
                                                }}
                                                className={`w-full group/item flex items-center gap-2.5 p-2 rounded-xl border border-transparent transition-all duration-500 ${styles.bg} ${styles.hover} active:scale-95 outline-none hover:border-white/50 shadow-sm`}
                                            >
                                                <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white shadow-sm flex items-center justify-center ${styles.color} group-hover/item:scale-110 transition-all duration-500`}>
                                                    {React.cloneElement(styles.icon, { size: 14 })}
                                                </div>
                                                <div className="flex-1 text-left min-w-0">
                                                    <p className={`text-[10px] md:text-[11px] font-black truncate poppins ${styles.color}`}>
                                                        {sub.title}
                                                    </p>
                                                    <p className="text-[7px] text-gray-500 font-bold uppercase tracking-widest mt-0.5 opacity-60">
                                                        {styles.label}
                                                    </p>
                                                </div>
                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center bg-white shadow-sm opacity-0 group-hover/item:opacity-100 -translate-x-1 group-hover/item:translate-x-0 transition-all duration-500 ${styles.color}`}>
                                                    <FiArrowRight size={10} />
                                                </div>
                                            </button>
                                        );
                                    })}

                                    {lesson.recording_url && (
                                        <button
                                            onClick={() => {
                                                setActiveContent({
                                                    type: 'video',
                                                    url: lesson.recording_url,
                                                    title: `${lesson.title} - Full Session`,
                                                    description: lesson.description
                                                });
                                                setIsModalOpen(true);
                                            }}
                                            className="w-full group/item flex items-center gap-2.5 p-2 rounded-xl border border-blue-100 bg-blue-50/50 hover:bg-blue-600 hover:text-white transition-all duration-500 active:scale-95 outline-none hover:shadow-md"
                                        >
                                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-blue-600 group-hover/item:scale-110 transition-all duration-500">
                                                <FiVideo size={14} />
                                            </div>
                                            <div className="flex-1 text-left">
                                                <p className="text-[10px] md:text-[11px] font-black poppins group-hover/item:text-white transition-colors">Main Class Video</p>
                                                <p className="text-[7px] font-bold uppercase tracking-widest mt-0.5 opacity-60 group-hover/item:text-blue-100">Watch Session</p>
                                            </div>
                                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-white shadow-sm opacity-0 group-hover/item:opacity-100 -translate-x-1 group-hover/item:translate-x-0 transition-all duration-500 text-blue-600">
                                                <FiArrowRight size={10} />
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {lesson.description && (
                                <div className="mt-auto px-4 py-3 bg-gray-50/20 border-t border-gray-50/50">
                                    <div className="flex items-start gap-1.5">
                                        <FiInfo size={10} className="text-gray-400 mt-0.5" />
                                        <p className="text-[9px] md:text-[10px] text-gray-500 leading-relaxed poppins font-medium italic line-clamp-1">
                                            {lesson.description}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Empty State Compact */}
                {lessons.length === 0 && (
                    <div className="text-center py-12 md:py-20 bg-white/40 glass border-2 border-dashed border-gray-200/60 rounded-3xl backdrop-blur-3xl animate-in zoom-in-95 duration-1000">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-white shadow-lg rounded-2xl flex items-center justify-center mx-auto mb-4 text-gray-200">
                            <FiActivity size={24} />
                        </div>
                        <h2 className="text-lg md:text-xl font-black text-gray-900 lemon-milk uppercase tracking-tighter px-6">Curriculum preparing</h2>
                        <p className="text-[11px] md:text-xs text-gray-500 mt-2 poppins max-w-xs mx-auto px-8 font-medium">
                            Academic materials are currently being uploaded.
                        </p>
                    </div>
                )}

                {/* Lesson Content Modal */}
                <LessonContentModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    content={activeContent}
                    securitySettings={securitySettings}
                />
            </div>
        </div>
    );
}

